import React, { useState, useEffect, useCallback } from 'react';
import { Loader2, Save, MessageCircle } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { api } from '../../lib/api';
import { formatDate } from '../../components/Shared';
import { toast } from 'sonner';

const CHAT_STATUSES = {
  new: { label: 'New', cls: 'bg-slate-100 text-slate-800 border-slate-300' },
  needs_response: { label: 'Needs Response', cls: 'bg-amber-50 text-amber-900 border-amber-300' },
  responded: { label: 'Responded', cls: 'bg-emerald-50 text-emerald-900 border-emerald-300' },
  converted_to_issue: { label: 'Converted to Issue', cls: 'bg-[#EAF2FF] text-[#1a2744] border-[#CFE0FF]' },
  archived: { label: 'Archived', cls: 'bg-slate-200 text-slate-600 border-slate-300' },
};

/* Field wrapper at module scope on purpose - defining it inside the component
   recreated it on every keystroke and made the text boxes lose focus after
   each letter. */
const F = ({ label, help, children }) => (
  <div>
    <label className="block font-bold text-[#1a2744] mb-1.5 text-sm">{label}</label>
    {children}
    {help && <p className="text-xs text-slate-400 mt-1">{help}</p>}
  </div>
);

const SettingsTab = () => {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get('/admin/ai-helper-settings').then((r) => setS(r.data)); }, []);
  if (!s) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  const save = async () => {
    setBusy(true);
    try {
      await api.put('/admin/ai-helper-settings', s);
      toast.success('AI Helper settings saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-5 space-y-4" data-testid="ai-helper-settings">
      <Card><CardContent className="p-4 space-y-4">
        <F label="Welcome message" help="First message visitors see when they open the helper.">
          <Textarea value={s.welcome_message || ''} onChange={(e) => setS({ ...s, welcome_message: e.target.value })} rows={3} data-testid="ai-settings-welcome" />
        </F>
        <F label="Disclaimer (shown under the chat input)" help="Must make clear this is NOT an official City of Kawartha Lakes assistant.">
          <Textarea value={s.disclaimer || ''} onChange={(e) => setS({ ...s, disclaimer: e.target.value })} rows={2} data-testid="ai-settings-disclaimer" />
        </F>
        <F label="Fallback response (when the AI doesn't know)">
          <Textarea value={s.fallback_response || ''} onChange={(e) => setS({ ...s, fallback_response: e.target.value })} rows={2} data-testid="ai-settings-fallback" />
        </F>
        <F label="Escalation message (after a human follow-up request)">
          <Textarea value={s.escalation_message || ''} onChange={(e) => setS({ ...s, escalation_message: e.target.value })} rows={2} data-testid="ai-settings-escalation" />
        </F>
        <F label="Suggested questions (one per line, max 6)" help="Shown as tap-to-ask chips at the start of a conversation.">
          <Textarea value={(s.suggested_questions || []).join('\n')} onChange={(e) => setS({ ...s, suggested_questions: e.target.value.split('\n').filter(Boolean).slice(0, 6) })} rows={5} data-testid="ai-settings-questions" />
        </F>
        <F label="Topic restrictions" help="What the AI may and may not talk about.">
          <Textarea value={s.topic_restrictions || ''} onChange={(e) => setS({ ...s, topic_restrictions: e.target.value })} rows={2} data-testid="ai-settings-topics" />
        </F>
        <F label="AI system prompt override" help="Leave blank to use the built-in default (recommended). The default enforces cautious wording for allegations, correct naming (Steve Lesperance, Ward 8, Councillor), and privacy rules.">
          <Textarea value={s.system_prompt || ''} onChange={(e) => setS({ ...s, system_prompt: e.target.value })} rows={5} placeholder="(using built-in default prompt)" data-testid="chat-prompt-input" />
        </F>
      </CardContent></Card>
      <Button onClick={save} disabled={busy} className="h-12 w-full sm:w-auto bg-[#1a2744] text-white font-bold btn-press" data-testid="ai-settings-save">
        {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />} Save AI Helper Settings
      </Button>
    </div>
  );
};

const SessionsTab = () => {
  const [sessions, setSessions] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    api.get('/admin/chat/sessions').then((r) => setSessions(r.data || [])).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const openSession = async (sid) => {
    const res = await api.get(`/admin/chat/sessions/${sid}`);
    setDetail(res.data);
  };

  const setStatus = async (sid, status) => {
    try {
      await api.put(`/admin/chat/sessions/${sid}/status`, { status });
      toast.success(`Marked as ${CHAT_STATUSES[status].label}`);
      load();
      if (detail?.session_id === sid) setDetail({ ...detail, workflow_status: status });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const filtered = sessions.filter((s) => statusFilter === 'all' || (s.workflow_status || 'new') === statusFilter);

  return (
    <div className="mt-5">
      <div className="bg-[#EAF2FF] border border-[#CFE0FF] rounded-xl p-3 mb-4">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="h-11 bg-white max-w-xs" data-testid="chat-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {Object.entries(CHAT_STATUSES).map(([v, { label }]) => <SelectItem key={v} value={v}>{label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {loading ? <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div> : (
        <div className="space-y-2">
          {filtered.length === 0 && <Card><CardContent className="p-8 text-center text-slate-500">No conversations in this status.</CardContent></Card>}
          {filtered.map((s) => {
            const st = CHAT_STATUSES[s.workflow_status || 'new'] || CHAT_STATUSES.new;
            return (
              <Card key={s.session_id} className="card-lift" data-testid="chat-session-row">
                <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                  <button onClick={() => openSession(s.session_id)} className="flex items-center gap-3 flex-1 min-w-0 text-left">
                    <MessageCircle className="h-5 w-5 text-[#C9A84C] shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-[#1a2744] truncate">{s.first_message || '(empty conversation)'}</p>
                      <p className="text-xs text-slate-500">{s.message_count} messages · {formatDate(s.updated_at)}</p>
                    </div>
                    <Badge variant="outline" className={`${st.cls} text-[11px] font-bold shrink-0`}>{st.label}</Badge>
                  </button>
                  <Select value={s.workflow_status || 'new'} onValueChange={(v) => setStatus(s.session_id, v)}>
                    <SelectTrigger className="h-10 w-full sm:w-48 bg-white" data-testid="chat-session-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(CHAT_STATUSES).map(([v, { label }]) => <SelectItem key={v} value={v}>{label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="chat-session-dialog">
          <DialogHeader><DialogTitle className="font-serif text-[#1a2744]">Conversation</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {(detail?.messages || []).map((m, i) => (
              <div key={i} className={`rounded-xl p-3 text-sm ${m.role === 'user' ? 'bg-[#EAF2FF] ml-6' : 'bg-slate-50 mr-6'}`}>
                <p className="text-[10px] font-bold uppercase text-slate-400 mb-1">{m.role === 'user' ? 'Resident' : 'Assistant'}</p>
                <p className="whitespace-pre-wrap text-slate-800">{m.content}</p>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default function AdminChat() {
  const [tab, setTab] = useState('sessions');
  return (
    <div data-testid="admin-chat">
      <h1 className="font-serif font-black text-2xl text-[#1a2744] mb-1">AI Helper / Chat Logs</h1>
      <p className="text-sm text-slate-500 mb-4">Review resident conversations, track which need a response, and tune the AI Helper's behaviour.</p>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white border border-slate-200 h-12 p-1">
          <TabsTrigger value="sessions" className="h-10 px-5 font-bold data-[state=active]:bg-[#1a2744] data-[state=active]:text-white" data-testid="chat-tab-sessions">Chat Logs</TabsTrigger>
          <TabsTrigger value="settings" className="h-10 px-5 font-bold data-[state=active]:bg-[#1a2744] data-[state=active]:text-white" data-testid="chat-tab-settings">AI Helper Settings</TabsTrigger>
        </TabsList>
      </Tabs>
      {tab === 'sessions' ? <SessionsTab /> : <SettingsTab />}
    </div>
  );
}
